import { API_BASE_URL } from '../const/constants.js';
import { v4 as uuidv4 } from 'uuid';

/**
 * 从文件名中解析后缀（含点号），如 "upload.pptx" -> ".pptx"
 * @param {string} fileName
 * @returns {string} 后缀（含点号），若无则返回空字符串
 */
function extractFileExt(fileName) {
  if (!fileName || typeof fileName !== 'string') return '';
  const idx = fileName.lastIndexOf('.');
  if (idx < 0 || idx === fileName.length - 1) return '';
  return fileName.substring(idx).toLowerCase();
}

/**
 * 根据文件后缀推断 Content-Type
 * @param {string} ext - 含点号后缀，如 ".pptx"
 * @returns {string}
 */
function getContentTypeByExt(ext) {
  const MIME_MAP = {
    '.pdf': 'application/pdf',
    '.doc': 'application/msword',
    '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.ppt': 'application/vnd.ms-powerpoint',
    '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    '.xls': 'application/vnd.ms-excel',
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.md': 'text/markdown',
    '.txt': 'text/plain',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.bmp': 'image/bmp',
    '.gif': 'image/gif',
    '.webp': 'image/webp',
  };
  return MIME_MAP[(ext || '').toLowerCase()] || 'application/octet-stream';
}

/**
 * 获取 COS 预签名 URL
 * @param {Object} [opts]
 * @param {string} [opts.fileExt] - 文件后缀（含点号，如 ".pptx"）
 * @param {string} [opts.requestId] - 请求 ID
 * @returns {Promise<{ uploadUrl: string, downloadUrl: string }>}
 */
async function getPresignedUrl({ fileExt = '', requestId = uuidv4() } = {}) {
  const body = { requestId, fileExt };
  console.log('[COS Upload] getPresignedUrl 请求参数:', body);
  const res = await fetch(`${API_BASE_URL}demo-user/getPresignedUrl`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (data.code === 0 && data.data) {
    return {
      uploadUrl: data.data.uploadUrl,
      downloadUrl: data.data.downloadUrl,
    };
  }
  throw new Error(data.message || '获取预签名URL失败');
}

/**
 * Base64 转 Blob
 */
function base64ToBlob(base64Data, contentType = 'application/octet-stream') {
  const pureBase64 = base64Data.replace(/^data:.+?;base64,/, '');
  const byteCharacters = atob(pureBase64);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    byteArrays.push(new Uint8Array(byteNumbers));
  }

  return new Blob(byteArrays, { type: contentType });
}

/**
 * 上传文件到 COS（通过预签名 URL）并返回下载地址
 * @param {Object} options
 * @param {string} options.base64 - 文件的 Base64 数据（可带或不带 data: 前缀）
 * @param {string} [options.contentType] - 文件 MIME 类型
 * @param {string} [options.fileName] - 原始文件名（用于解析后缀，如 upload.pptx）
 * @param {string} [options.fileExt] - 显式指定后缀（含点号，如 ".pptx"），优先级高于 fileName
 * @returns {Promise<string>} COS 文件下载 URL
 */
export async function uploadToCos({ base64, contentType, fileName, fileExt }) {
  const ext = fileExt || extractFileExt(fileName);
  // 优先使用显式传入的 contentType，否则根据后缀自动推断
  const finalContentType = contentType || getContentTypeByExt(ext);
  console.log('[COS Upload] 文件后缀:', ext, 'Content-Type:', finalContentType, '(来源:', fileExt ? 'fileExt' : `fileName=${fileName}`, ')');

  const { uploadUrl, downloadUrl } = await getPresignedUrl({ fileExt: ext });
  console.log('[COS Upload] 预签名URL:', { uploadUrl: uploadUrl?.substring(0, 80), downloadUrl: downloadUrl?.substring(0, 80) });

  const blob = base64ToBlob(base64, finalContentType);
  console.log('[COS Upload] Blob大小:', blob.size, 'bytes');

  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': finalContentType },
    body: blob,
  });

  console.log('[COS Upload] PUT响应:', putRes.status, putRes.statusText);

  if (!putRes.ok) {
    const errText = await putRes.text();
    console.error('[COS Upload] PUT失败:', errText);
    throw new Error(`COS上传失败: ${putRes.status} ${putRes.statusText}`);
  }

  return downloadUrl;
}

/**
 * 判断是否需要先上传 COS
 */
export function needCosUpload(action) {
  const COS_UPLOAD_ACTIONS = ['MultimodalDocParse'];
  return COS_UPLOAD_ACTIONS.includes(action);
}
